import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';

// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

// Box icon for batch modal
const BoxIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

// Barcode icon for serial modal
const BarcodeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1z" />
  </svg>
);

const API_URL = '/api';

// Batch Info Modal Component (unchanged)
const BatchInfoModal = ({ isOpen, onClose, batches = [], productName = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BoxIcon />
              </div>
              <h3 className="text-base font-medium text-gray-800">Batch Details</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex items-center">
            <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
            <span className="text-xs font-medium text-gray-800 ">{productName || "N/A"}</span>
          </div>
        </div>
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {batches.length > 0 ? (
                  batches.map((batch, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {batch.BatchNumber || batch.batchNumber || '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-700">
                          {batch.ExpiryDate || batch.expiryDate 
                            ? dayjs(batch.ExpiryDate || batch.expiryDate).format('YYYY-MM-DD') 
                            : '-'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-800">
                          {batch.Quantity || batch.quantity || '0'}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BoxIcon className="text-gray-300 text-xl mb-1" />
                        <span>No batch information available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {batches.length} batch{batches.length !== 1 ? 'es' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Serial Info Modal Component (unchanged)
const SerialInfoModal = ({ isOpen, onClose, serialNumbers = [], productName = "", locationCode = "" }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[80vh] flex flex-col border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="bg-gray-300 p-1.5 rounded">
                <BarcodeIcon />
              </div>
              <h3 className="text-base font-medium text-gray-800">Serial Numbers</h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Product:</span>
              <span className="text-xs font-medium text-gray-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Location:</span>
              <span className="text-xs font-medium text-gray-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-gray-600 mr-1">Count:</span>
              <span className="text-xs font-medium text-gray-800">{serialNumbers.length}</span>
            </div>
          </div>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-0">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {serialNumbers.length > 0 ? (
                  serialNumbers.map((serial, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100'}>
                      <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-gray-700">
                        {index + 1}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-mono font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
                          {serial}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">
                        {productName || "N/A"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {locationCode || "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-xs text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <BarcodeIcon className="text-gray-300 text-xl mb-1" />
                        <span>No serial numbers available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {serialNumbers.length} serial number{serialNumbers.length !== 1 ? 's' : ''} displayed
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-gray-500 transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to extract serial numbers from an item
// Helper function to extract serial numbers from an item
const getSerialNumbers = (item) => {
  let serialNumbers = [];

  // Check for direct SerialNumbers or serials array
  if (item.SerialNumbers && Array.isArray(item.SerialNumbers)) {
    serialNumbers = item.SerialNumbers;
  } else if (item.serials && Array.isArray(item.serials)) {
    serialNumbers = item.serials;
  }

  // Check for Serials array (without the 'N') - THIS IS THE MISSING PART
  if (serialNumbers.length === 0 && item.Serials && Array.isArray(item.Serials)) {
    // Extract the SerialNumber property from each object
    serialNumbers = item.Serials.map(serialObj => serialObj.SerialNumber || serialObj.serialNumber);
  } else if (serialNumbers.length === 0 && item.serials && Array.isArray(item.serials)) {
    // Handle lowercase version
    serialNumbers = item.serials.map(serialObj => serialObj.SerialNumber || serialObj.serialNumber);
  }

  // Check for stringified JSON
  if (serialNumbers.length === 0) {
    if (item.SerialNumbers && typeof item.SerialNumbers === 'string') {
      try {
        const parsed = JSON.parse(item.SerialNumbers);
        if (Array.isArray(parsed)) {
          serialNumbers = parsed;
        }
      } catch (e) {
        console.error('Error parsing SerialNumbers string:', e);
      }
    } else if (item.serials && typeof item.serials === 'string') {
      try {
        const parsed = JSON.parse(item.serials);
        if (Array.isArray(parsed)) {
          serialNumbers = parsed;
        }
      } catch (e) {
        console.error('Error parsing serials string:', e);
      }
    }
  }

  // Check for TrackingData - enhanced detection
  if (serialNumbers.length === 0 && item.TrackingData) {
    // TrackingData might be an array of tracking entries
    if (Array.isArray(item.TrackingData)) {
      const trackingSerials = item.TrackingData
        .filter(tracking => {
          // Check TrackingType: could be 'serial', 'Serial', 2, or '2'
          const trackingType = tracking.TrackingType;
          return trackingType === 'serial' || trackingType === 'Serial' || 
                 trackingType === 2 || trackingType === '2';
        })
        .map(tracking => {
          // Check for different property names
          return tracking.TrackingNumber || tracking.SerialNumber || 
                 tracking.trackingNumber || tracking.serialNumber;
        });
      if (trackingSerials.length > 0) {
        serialNumbers = trackingSerials;
      }
    } else if (typeof item.TrackingData === 'object') {
      // If TrackingData is an object, it might have a 'serials' property or similar
      if (item.TrackingData.serials && Array.isArray(item.TrackingData.serials)) {
        serialNumbers = item.TrackingData.serials;
      } else if (item.TrackingData.Serials && Array.isArray(item.TrackingData.Serials)) {
        serialNumbers = item.TrackingData.Serials;
      }
    }
  }

  // Check for nested tracking data structure
  if (serialNumbers.length === 0 && item.ItemTracking) {
    if (Array.isArray(item.ItemTracking)) {
      const itemTrackingSerials = item.ItemTracking
        .filter(tracking => {
          const trackingType = tracking.TrackingType;
          return trackingType === 'serial' || trackingType === 'Serial' || 
                 trackingType === 2 || trackingType === '2';
        })
        .map(tracking => tracking.TrackingNumber || tracking.SerialNumber);
      if (itemTrackingSerials.length > 0) {
        serialNumbers = itemTrackingSerials;
      }
    }
  }

  // Check if the item has a tracking type property
  if (serialNumbers.length === 0 && item.TrackingType) {
    // If tracking type is serial (2), we should show a message that serials exist but aren't detailed
    if (item.TrackingType === 'serial' || item.TrackingType === 'Serial' || 
        item.TrackingType === 2 || item.TrackingType === '2') {
      // Return a placeholder array to indicate serial tracking exists
      return ['Serial tracking enabled'];
    }
  }

  return serialNumbers;
};

// Helper function to extract batch numbers from an item
const getBatchNumbers = (item) => {
  let batchNumbers = [];

  // Check for direct Batches or batches array
  if (item.Batches && Array.isArray(item.Batches)) {
    batchNumbers = item.Batches;
  } else if (item.batches && Array.isArray(item.batches)) {
    batchNumbers = item.batches;
  }

  // Check for stringified JSON
  if (batchNumbers.length === 0) {
    if (item.Batches && typeof item.Batches === 'string') {
      try {
        const parsed = JSON.parse(item.Batches);
        if (Array.isArray(parsed)) {
          batchNumbers = parsed;
        }
      } catch (e) {
        console.error('Error parsing Batches string:', e);
      }
    } else if (item.batches && typeof item.batches === 'string') {
      try {
        const parsed = JSON.parse(item.batches);
        if (Array.isArray(parsed)) {
          batchNumbers = parsed;
        }
      } catch (e) {
        console.error('Error parsing batches string:', e);
      }
    }
  }

  // Check for TrackingData
  if (batchNumbers.length === 0 && item.TrackingData) {
    if (Array.isArray(item.TrackingData)) {
      const trackingBatches = item.TrackingData
        .filter(tracking => {
          const trackingType = tracking.TrackingType;
          return trackingType === 'batch' || trackingType === 'Batch' || 
                 trackingType === 1 || trackingType === '1';
        })
        .map(tracking => ({
          BatchNumber: tracking.TrackingNumber || tracking.BatchNumber,
          ExpiryDate: tracking.ExpiryDate,
          Quantity: tracking.Quantity
        }));
      if (trackingBatches.length > 0) {
        batchNumbers = trackingBatches;
      }
    }
  }

  // Check if the item has a tracking type property
  if (batchNumbers.length === 0 && item.TrackingType) {
    // If tracking type is batch (1), we should show a message that batches exist but aren't detailed
    if (item.TrackingType === 'batch' || item.TrackingType === 'Batch' || 
        item.TrackingType === 1 || item.TrackingType === '1') {
      // Return a placeholder array to indicate batch tracking exists
      return ['Batch tracking enabled'];
    }
  }

  return batchNumbers;
};

const StockOutViewModal = ({ open, onClose, stockOutId }) => {
  const [stockOutDetails, setStockOutDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    locations: [],
    branches: [],
  });
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const modalRef = useRef(null);

  // State for batch and serial modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [currentItemBatches, setCurrentItemBatches] = useState([]);
  const [currentItemSerials, setCurrentItemSerials] = useState([]);
  const [currentItemName, setCurrentItemName] = useState('');
  const [currentItemLocation, setCurrentItemLocation] = useState('');

  const SCREEN_NAME = 'StockOut';
  
  // Handle click outside to close when minimized
  useEffect(() => {
    if (isMinimized && open) {
      const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
          onClose();
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMinimized, open, onClose]);
  
  useEffect(() => {
    if (open) {
      fetchDropdownData();
      fetchScreenConfig();
      
      if (stockOutId?.StockOutID || stockOutId?.stockOutId) {
        fetchStockOutDetails(stockOutId.StockOutID || stockOutId.stockOutId);
      }
    } else {
      // Reset states when modal is closed
      setIsMaximized(false);
      setIsMinimized(false);
    }
  }, [open, stockOutId]);

  const fetchScreenConfig = async () => {
  try {
    const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!res.ok) {
      let body = null;
      try { body = await res.json(); } catch (e) { /* ignore */ }
      const msg = body && body.message ? body.message : `Server returned ${res.status}`;
      throw new Error(msg);
    }

    const data = await res.json();
    console.log("Screen  info....." + Array.isArray(data) ? (data[0] || null) : data);
    return Array.isArray(data) ? (data[0] || null) : data;
  
  } catch (err) {
    console.error('fetchScreenConfig error:', err);
    throw err;
  }
};

// Helper to determine if Department should show in header
const shouldShowDepartmentOnHeader = (screenConfig) => {
  if (!screenConfig) return false;

  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  const showHeader = truthy(screenConfig.ShowOnHeader) || truthy(screenConfig.showOnHeader);
  const showScreen = truthy(screenConfig.ShowOnScreen) || truthy(screenConfig.showOnScreen);

  // ✅ only true if BOTH are truthy
  return showHeader && showScreen;
};

// Helper to determine if Department should show in items
const shouldShowDepartmentOnItems = (screenConfig) => {
  if (!screenConfig) return false;

  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };

  const showScreen = truthy(screenConfig.ShowOnScreen) || truthy(screenConfig.showOnScreen);
  const showItem = truthy(screenConfig.ShowOnItem) || truthy(screenConfig.showOnItem);

  // ✅ only true if BOTH are truthy
  return showScreen && showItem;
};


  
  const fetchDropdownData = async () => {
    try {
      const response = await axios.get(`${API_URL}/stockout/form-data`);
      setDropdownData({
        users: response.data.users || [],
        locations: response.data.locations || [],
        branches: response.data.branches || [],

      });
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };
  // Helper function to get branch name
const getBranchName = (branchCode) => {
  if (!branchCode) return '-';
  const branch = dropdownData.branches.find(branch => branch.BranchCode === branchCode);
  return branch ? branch.BranchName : branchCode;
};
  const fetchStockOutDetails = async (stockOutID) => {
    setLoading(true);
    setError(null);
    try {
      console.log(`Fetching StockOut details for: ${stockOutID}`);
      // Add a timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API_URL}/stockout/${stockOutID}?t=${timestamp}`);
      console.log('StockOut details:', response.data);
      
      // Log the status value for debugging
      console.log('Status value (Status):', response.data.Status, 'Type:', typeof response.data.Status);
      console.log('InventoryFlag value:', response.data.InventoryFlag, 'Type:', typeof response.data.InventoryFlag);
      
      setStockOutDetails(response.data);
    } catch (error) {
      console.error('Error fetching StockOut details:', error);
      setError('Failed to load stock out details');
      setStockOutDetails(null);
    }
    setLoading(false);
  };
  
  // Helper function to get location name
  const getLocationName = (locationCode) => {
    if (!locationCode) return '-';
    const location = dropdownData.locations.find(loc => loc.LocationCode === locationCode);
    return location ? location.LocationName : locationCode;
  };
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return parseFloat(value).toFixed(2);
  };
  
  // Format quantity to display as positive (since StockOut quantities are stored as negative)
  const formatQuantity = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return Math.abs(parseFloat(value)).toString();
  };
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
    setIsMinimized(false);
  };
  
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    setIsMaximized(false);
  };

  // Function to open batch modal
  const openBatchModal = (item) => {
    const batchNumbers = getBatchNumbers(item);
    console.log('Opening batch modal with data:', batchNumbers);
    setCurrentItemBatches(batchNumbers);
    setCurrentItemName(item.ItemName || item.itemName || '');
    setShowBatchModal(true);
  };

  // Function to open serial modal
  const openSerialModal = (item) => {
    const serialNumbers = getSerialNumbers(item);
    console.log('Opening serial modal with data:', serialNumbers);
    setCurrentItemSerials(serialNumbers);
    setCurrentItemName(item.ItemName || item.itemName || '');
    setCurrentItemLocation(item.Location || item.location || '');
    setShowSerialModal(true);
  };
  
  if (!open || !stockOutId) return null;
  
  // Use stockOutDetails if available, otherwise use stockOutId
  const stockOut = stockOutDetails || stockOutId;
  const stockOutItems = stockOutDetails?.Items || stockOutId?.Items || [];
  
  // Inline status determination
  const isActive = stockOut.Status === 1 || stockOut.Status === true;
  const statusText = isActive ? 'Active' : 'Inactive';
  const statusStyle = isActive 
    ? 'bg-green-100 text-green-800' 
    : 'bg-yellow-100 text-yellow-800';
    
  // Inventory flag determination
  const hasInventoryFlag = stockOut.InventoryFlag === 1 || stockOut.InventoryFlag === true;
  const inventoryFlagText = hasInventoryFlag ? 'Yes' : 'No';
  const inventoryFlagStyle = hasInventoryFlag 
    ? 'bg-blue-100 text-blue-800' 
    : 'bg-gray-100 text-gray-800';
  
  console.log('Status determination - Status:', stockOut.Status, 'Type:', typeof stockOut.Status, 'isActive:', isActive, 'statusText:', statusText);
  console.log('InventoryFlag determination - InventoryFlag:', stockOut.InventoryFlag, 'Type:', typeof stockOut.InventoryFlag, 'hasInventoryFlag:', hasInventoryFlag, 'inventoryFlagText:', inventoryFlagText);
  
  // Modal classes based on state
  const modalClasses = `
    bg-white shadow-2xl w-full flex flex-col transition-all duration-300 ease-in-out
    ${isMaximized ? 'fixed inset-4 max-w-none' : 'max-w-6xl h-full'}
    ${isMinimized ? 'h-16' : 'h-full'}
    ${open ? "translate-x-0" : "translate-x-full"}
  `;
  
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${
        open ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
     <div
         className={`bg-white shadow-2xl ${isMaximized ? 'w-full h-full' : 'w-full max-w-6xl h-full'} flex flex-col
            transition-transform transform-gpu duration-300 ease-in-out
            ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">
              Stock Out Details
            </h2>
            <div className="flex space-x-2">
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1"
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
                onClick={onClose}
              >
                <CloseIcon size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content Area - Hidden when minimized */}
        {!isMinimized && (
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading stock out details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
                Error: {error}
              </div>
            ) : (
              <div className="space-y-6 pb-6">
                {/* StockOut Header */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <h3 className="text-lg font-semibold text-[#384959] mb-4">
                    Stock Out Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Stock Out ID
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {stockOut.StockOutID || stockOut.stockOutId || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Stock Out Date
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {stockOut.StockOutDate ? dayjs(stockOut.StockOutDate).format('YYYY-MM-DD') : '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Stock Out By User ID
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {stockOut.StockOutByUserID || stockOut.stockOutByUserId || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Stock Out By User Name
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                        {stockOut.StockOutUserName || stockOut.stockOutUserName || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Branch
  </label>
  <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
    {stockOut.BranchCode ? `${stockOut.BranchCode} - ${getBranchName(stockOut.BranchCode)}` : '-'}
  </div>
</div>
{shouldShowDepartmentOnHeader(stockOut.screenConfig) &&
  (stockOut.DepartmentName || stockOut.DepartmentCode || stockOut.DepartmentID) && (
    <div className="flex flex-col gap-2">
      <label className="block text-sm font-medium text-[#64748b]">Department</label>
      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
        {stockOut.DepartmentName
          ? `${stockOut.DepartmentCode ? stockOut.DepartmentCode + ' - ' : ''}`
          : (stockOut.DepartmentCode || stockOut.DepartmentID || '-')}
      </div>
    </div>
)}

                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Status
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center ">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${statusStyle}`}>
                          {statusText}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Update Inventory
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] flex items-center">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${inventoryFlagStyle}`}>
                          {inventoryFlagText}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Total Quantity
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0]">
                        {formatQuantity(stockOut.TotalQuantity || stockOut.totalQuantity)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 md:col-span-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Note
                      </label>
                      <div className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] min-h-[60px] uppercase">
                        {stockOut.Note || stockOut.note || '-'}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* StockOut Items */}
                <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[#384959]">
                      Stock Out Items
                    </h3>
                  </div>
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full border border-[#E2E8F0]">
                      <thead>
                        <tr className="bg-[#BDDDFC] text-[#384959]">
                          <th className="p-2">#</th>
                          <th className="p-2">Item Code</th>
                          <th className="p-2">Item Name</th>
                          <th className="p-2">UOM</th>
                          <th className="p-2">Quantity</th>
                          <th className="p-2">Rate</th>
                          <th className="p-2">Value</th>
                          <th className="p-2">Warehouse</th>
                          <th className="p-2">Reason</th>
                          {shouldShowDepartmentOnItems(stockOut.screenConfig) && (
      <th className="p-2">Department</th>
    )}
                          <th className="p-2">Tracking Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockOutItems.length > 0 ? (
                          stockOutItems.map((item, idx) => {
                            // Use helper functions to get tracking data
                            const batchNumbers = getBatchNumbers(item);
                            const serialNumbers = getSerialNumbers(item);
                            
                            const hasBatches = batchNumbers.length > 0;
                            const hasSerials = serialNumbers.length > 0;
                            
                            // Debug logging for the first item
                            if (idx === 0) {
                              console.log('Item tracking data:', {
                                itemCode: item.ItemCode,
                                itemName: item.ItemName,
                                batches: batchNumbers,
                                serials: serialNumbers,
                                trackingType: item.TrackingType,
                                trackingData: item.TrackingData,
                                hasBatches,
                                hasSerials
                              });
                            }
                            
                            return (
                              <tr key={idx} className="border-t border-[#E2E8F0]">
                                <td className="p-2 text-center">{idx + 1}</td>
                                <td className="p-2">
                                  <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {item.ItemCode || item.itemCode || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.ItemName || item.itemName || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.UOM || item.uom || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {formatQuantity(item.QuantityMinus || item.quantityMinus)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {formatCurrency(item.Rate || item.rate)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] font-semibold">
                                    {formatCurrency(item.Value || item.value)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {getLocationName(item.Location || item.location)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
                                    {item.Reason || item.reason || '-'}
                                  </div>
                                </td>
                                {shouldShowDepartmentOnItems(stockOut.screenConfig) && (
          <td className="p-2">
            <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] uppercase">
              {item.DepartmentCode || '-'}
            </div>
          </td>
        )}
                                <td className="p-2">
                                  <div className="flex flex-col space-y-1">
                                    {hasBatches && (
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                        onClick={() => openBatchModal(item)}
                                      >
                                        View Batches ({batchNumbers.length})
                                      </button>
                                    )}
                                    {hasSerials && (
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                        onClick={() => openSerialModal(item)}
                                      >
                                        View Serials ({serialNumbers.length})
                                      </button>
                                    )}
                                    {!hasBatches && !hasSerials && (
                                      <span className="text-xs text-gray-500 text-center">No tracking</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={shouldShowDepartmentOnItems(stockOut.screenConfig) ? 11 : 10} className="text-center text-gray-500 py-4">
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Total Summary Section */}
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                    <h4 className="text-md font-semibold text-[#384959] mb-4">
                      Total Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Quantity</span>
                        <span className="text-base font-semibold">
                          {formatQuantity(stockOut.TotalQuantity || stockOut.totalQuantity)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">Total Value</span>
                        <span className="text-base font-semibold text-blue-600">
                          {formatCurrency(
                            stockOutItems.reduce((sum, item) => {
                              const value = parseFloat(item.Value || item.value) || 0;
                              return sum + Math.abs(value); // Use absolute value for display
                            }, 0)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Transaction Entries Section - Only show if InventoryFlag is true */}
                {/* {hasInventoryFlag && (
                  <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-[#384959]">
                        Transaction Entries
                      </h3>
                    </div>
                    <div className="overflow-x-auto custom-scrollbar">
                      <table className="min-w-full border border-[#E2E8F0]">
                        <thead>
                          <tr className="bg-[#BDDDFC] text-[#384959]">
                            <th className="p-2">#</th>
                            <th className="p-2">Transaction ID</th>
                            <th className="p-2">Item Code</th>
                            <th className="p-2">Quantity</th>
                            <th className="p-2">Warehouse</th>
                            <th className="p-2">Rate</th>
                            <th className="p-2">Value</th>
                            <th className="p-2">Transaction Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockOutDetails?.TransactionEntries && stockOutDetails.TransactionEntries.length > 0 ? (
                            stockOutDetails.TransactionEntries.map((entry, idx) => (
                              <tr key={idx} className="border-t border-[#E2E8F0]">
                                <td className="p-2 text-center">{idx + 1}</td>
                                <td className="p-2">
                                  <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {entry.TransactionID || entry.transactionId || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {entry.ItemCode || entry.itemCode || '-'}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {formatQuantity(entry.Quantity || entry.quantity)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-32 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {entry.LocationName || entry.locationName || getLocationName(entry.WarehouseCode || entry.warehouseCode)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {formatCurrency(entry.Rate || entry.rate)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-16 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0] font-semibold">
                                    {formatCurrency(entry.Value || entry.value)}
                                  </div>
                                </td>
                                <td className="p-2">
                                  <div className="w-24 border rounded-lg px-2 py-1 bg-[#F1F5F9] border-[#E2E8F0]">
                                    {entry.TransactionDate ? dayjs(entry.TransactionDate).format('YYYY-MM-DD') : '-'}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={8} className="text-center text-gray-500 py-4">
                                No transaction entries found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )} */}
              </div>
            )}
          </div>
        )}
        
        {/* Fixed Footer with Button - Hidden when minimized */}
        {!isMinimized && (
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Batch Info Modal */}
      <BatchInfoModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        batches={currentItemBatches}
        productName={currentItemName}
      />

      {/* Serial Info Modal */}
      <SerialInfoModal
        isOpen={showSerialModal}
        onClose={() => setShowSerialModal(false)}
        serialNumbers={currentItemSerials}
        productName={currentItemName}
        locationCode={currentItemLocation}
      />
    </div>
  );
};

export default StockOutViewModal;